import type { FC } from "react";
import { useCallback, useState } from "react";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { Button } from "@/components/flexkit/Button";
import { useListServiceAccountTokens } from "@/queries/service-account-token-queries";
import { ServiceAccountTokensTable } from "@/components/settings/organization/service-accounts/ServiceAccountTokensTable";
import { ServiceAccountNewTokenModal } from "@/components/settings/organization/service-accounts/ServiceAccountNewTokenModal";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import type { ServiceAccountHeaders } from "@/api";

export type ServiceAccountTokensListProps = {
    headers?: ServiceAccountHeaders;
    serviceAccountId: string;
};

export const ServiceAccountTokensList: FC<ServiceAccountTokensListProps> = ({ headers, serviceAccountId }) => {
    const [showNewTokenModal, setShowNewTokenModal] = useState(false);

    const { data, isPending } = useListServiceAccountTokens({
        serviceAccountId,
        headers,
        enabled: !!headers,
    });

    const tokens = data?.tokens ?? [];

    const onNewToken = useCallback(() => {
        setShowNewTokenModal(true);
    }, []);

    if (isPending || !headers) {
        return <SkeletonBlock ready={false} failed={false} className="h-[100px] w-full" />;
    }

    return (
        <>
            {tokens.length === 0 ? (
                <EmptyStateCard
                    title="No tokens"
                    description="Create tokens to authenticate as this service account via the API."
                    data-testid="service-account-tokens-empty-state"
                >
                    <Button
                        onClick={onNewToken}
                        data-testid="add-service-account-token-button"
                        data-tracking-id="new-token-service-account-empty-state"
                    >
                        New Token
                    </Button>
                </EmptyStateCard>
            ) : (
                <div className="flex flex-col gap-2">
                    <div className="flex justify-end">
                        <Button
                            size="sm"
                            onClick={onNewToken}
                            data-testid="add-service-account-token-button"
                            data-tracking-id="new-token-service-account-list"
                        >
                            New Token
                        </Button>
                    </div>
                    <ServiceAccountTokensTable tokens={tokens} serviceAccountId={serviceAccountId} headers={headers} />
                </div>
            )}
            <ServiceAccountNewTokenModal
                open={showNewTokenModal}
                onClose={() => setShowNewTokenModal(false)}
                serviceAccountId={serviceAccountId}
                headers={headers}
            />
        </>
    );
};
